import axios from "axios";

const BASE = `${process.env.REACT_APP_API_URL}/api/professionals/video-call-appointments`;

const authHeaders = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` }
});

// Surface the actual HTTP status + server message so failures are diagnosable
// from the browser console instead of a generic "something failed".
const logApiError = (label: string, err: any) => {
  if (process.env.NODE_ENV === "production") return;
  // eslint-disable-next-line no-console
  console.error(
    `[video-appointments] ${label} failed —`,
    "status:",
    err?.response?.status,
    "body:",
    err?.response?.data ?? err?.message ?? err
  );
};

/**
 * Raw appointment shape ACTUALLY returned by the Web API (camelCase).
 * NOTE: the deployed API differs from video-call-appointments.openapi.yaml,
 * which documented snake_case (`scribo_partition`, `scheduled_date`, …).
 * The real fields are camelCase, confirmed from live responses.
 */
export interface ApiAppointment {
  id: string;
  externalId?: string;
  clinicalFileId: string | null;
  scheduledDate: string;
  expectedDuration: string;
  observations: string;
  state?: number | null;
  createdBy?: string;
  updatedBy?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  deleted?: boolean;
}

export interface CreateAppointmentBody {
  scheduledDate: string; // ISO 8601
  expectedDuration: string; // minutes, as string
  observations: string;
  clinicalFileId?: string;
}

/**
 * List appointments whose scheduledDate falls within [from, to] (includes
 * cancelled ones, surfaced via their CANCELLED state). `to` defaults to
 * `from + 24h` server-side when omitted, so we always pass it.
 */
export const listAppointments = async (
  token: string,
  from: string,
  to?: string
): Promise<ApiAppointment[]> => {
  const params: Record<string, string> = { from };
  if (to) params.to = to;
  try {
    const { data } = await axios.get(BASE, { params, ...authHeaders(token) });
    return data?.appointments ?? [];
  } catch (err) {
    logApiError("list", err);
    throw err;
  }
};

export const createAppointment = async (
  token: string,
  body: CreateAppointmentBody
): Promise<ApiAppointment> => {
  try {
    // The API wraps the created record just like the list endpoint does
    // ({ appointments: [...] } → { appointment: {...} }).
    const { data } = await axios.post(BASE, body, authHeaders(token));
    return data.appointment;
  } catch (err) {
    logApiError("create", err);
    throw err;
  }
};

export const cancelAppointment = async (
  token: string,
  id: string
): Promise<void> => {
  try {
    await axios.delete(`${BASE}/${id}`, authHeaders(token));
  } catch (err) {
    logApiError("cancel", err);
    throw err;
  }
};
